import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/context/auth-context'
import { ThemeProvider } from '@/context/theme-context'
import { SettingsProvider } from '@/context/settings-context'
import { LifeDomainsProvider } from '@/context/life-domains-context'
import { CalendarsProvider } from '@/context/calendars-context'
import { NotificationsProvider } from '@/context/notifications-context'
import { OnboardingProvider } from '@/context/onboarding-context'
import { ProtectedRoute } from '@/components/protected-route'
import { FeatureFlagRoute } from '@/components/feature-flag-route'
import { AppLayout } from '@/components/layouts/app-layout'
import { PWAInstallPrompt } from '@/components/pwa-install-prompt'
import { PWAUpdatePrompt } from '@/components/pwa-update-prompt'
import { PWAThemeSync } from '@/components/pwa-theme-sync'
import { ScrollToTop } from '@/components/scroll-to-top'
import { ExtensionAuthBridge } from '@/components/ExtensionAuthBridge'
import { SentryErrorBoundary } from '@/components/sentry-error-boundary'
import { LoginPage } from '@/pages/login'
import { AuthCallbackPage } from '@/pages/auth-callback'
import { OnboardingPage } from '@/pages/onboarding'
import { InsightsPage } from '@/pages/insights'
import { KobaPage } from '@/pages/koba'
import { PlanningPage } from '@/pages/planning'
import { SettingsPage } from '@/pages/settings'
import { AutomationExecutionsPage } from '@/pages/automation-executions'
import { Toaster } from '@/components/ui/toaster'
import { FeatureFlags } from '@/lib/feature-flags'

function App() {
  // Check if the app is being loaded from the browser extension
  const urlParams = new URLSearchParams(window.location.search)
  const fromExtension = urlParams.get('fromExtension') === '1'

  return (
    <SentryErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="calintent-theme">
        <AuthProvider>
          {/* Extension Auth Bridge - handles authentication from browser extension */}
          {fromExtension && <ExtensionAuthBridge />}
          
          <SettingsProvider>
            <CalendarsProvider>
              <NotificationsProvider>
                <LifeDomainsProvider>
                <OnboardingProvider>
                  <Router>
                    <ScrollToTop />
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/auth/callback" element={<AuthCallbackPage />} />
                      
                      {/* Onboarding route (protected, standalone page without AppLayout) */}
                      <Route 
                        path="/onboarding" 
                        element={
                          <ProtectedRoute>
                            <OnboardingPage />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* Protected routes with master layout */}
                      <Route 
                        path="/" 
                        element={
                          <ProtectedRoute>
                            <AppLayout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Navigate to="/insights" replace />} />
                        <Route path="insights" element={<InsightsPage />} />
                        <Route path="koba" element={
                          <FeatureFlagRoute flag={FeatureFlags.DEV_FEATURES_ACCESS}>
                            <KobaPage />
                          </FeatureFlagRoute>
                        } />
                        <Route path="planning" element={<PlanningPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="automation-executions" element={<AutomationExecutionsPage />} />
                      </Route>
                    </Routes>
                  </Router>
                  <Toaster />
                  <PWAInstallPrompt />
                  <PWAUpdatePrompt />
                  <PWAThemeSync />
                </OnboardingProvider>
              </LifeDomainsProvider>
              </NotificationsProvider>
            </CalendarsProvider>
          </SettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </SentryErrorBoundary>
  )
}

export default App